import { useSaveActions } from "../../common"
import { TableName } from "../../../tables"
import { CashFund, TransactionDestinationType, TransactionSourceType } from "../state/types"
import { useCashFundLoaders } from "../loaders/cashFundLoaders"
import { useTransactionService } from "../services/transactionService"
import { useCashFundBuilders } from "../builders/cashFundBuilders"

export const useCashFundActions = () => {
    const { buildCashFund } = useCashFundBuilders()
    
    const cashFundLoaders = useCashFundLoaders()
    
    const { createTransaction } = useTransactionService()
    const saveActions = useSaveActions()

    const saveCashFund = (cashFund: CashFund) => {
        const { save } = saveActions()
        return save(TableName.CASH_FUNDS, cashFund) as Promise<CashFund>
    }

    const updateAmount = async (
        cashFundId: string,
        availableAmountDiff = 0,
        reservedAmountDiff = 0
    ) => {
        const { loadCashFund } = cashFundLoaders()        
        const cashFund = await loadCashFund(cashFundId, true) as CashFund
        const updatedFund = buildCashFund({
            ...cashFund,
            availableAmount: cashFund.availableAmount + availableAmountDiff,
            reservedAmount: cashFund.reservedAmount + reservedAmountDiff
        })
        
        return saveCashFund(updatedFund)
    }

    const addToCashFund = async (
        cashFundId: string,
        amount: number
    ) => {
        return updateAmount(cashFundId, amount)
    }

    const substractFromCashFund = async (
        cashFundId: string,
        amount: number
    ) => {
        return updateAmount(cashFundId, -amount)
    }

    const reserveInCashFund = (
        cashFundId: string,
        amount: number
    ) => {
        return updateAmount(cashFundId, -amount, amount)
    }

    const substractReservedFromCashFund = (
        cashFundId: string,
        amount: number
    ) => {
        return updateAmount(cashFundId, 0, -amount)
    }

    const transferMoney = async (
        sourceType: TransactionSourceType,
        destinationType: TransactionDestinationType,
        amount: number,
        sourceId?: string,
        destinationId?: string,
        paymentDate?: Date,
        useReserved = false,
        concept?: string
    ) => {
        if (sourceType === TransactionSourceType.CASH_FUND && sourceId) {
            const substract = useReserved ? substractReservedFromCashFund : substractFromCashFund
            await substract(sourceId, amount)
        }
        if (destinationType === TransactionDestinationType.CASH_FUND && destinationId) {
            await addToCashFund(destinationId, amount)
        }
        
        const transactionData = {
            sourceType,
            sourceId,
            amount,
            destinationType,
            destinationId,
            date: (paymentDate || new Date()).toISOString(),
            concept
        }
        await createTransaction(transactionData)
    }

    const removeCashFund = (id: string) => {
        const { remove } = saveActions()
        return remove(TableName.CASH_FUNDS, id)
    }

    return {
        saveCashFund,
        addToCashFund,
        substractFromCashFund,
        reserveInCashFund,
        substractReservedFromCashFund,
        transferMoney,
        removeCashFund
    }
}
